// @flow
import { LocusApiModel } from 'common/modules/api';
import { type QueryResult, useQuery } from 'react-query';
import EnvConfig from 'config';
import { LocaleConfigResponse } from './models/internal/LocaleConfigResponse';
import type { LocaleConfigResponseModelType } from './models/flow/LocaleConfigResponse';
import queryClient from 'common/middleware/queryClient';
import useEffectOnce from 'common/hooks/useEffectOnce';
import { usePlatformSettings } from 'common/modules/platform-settings';
import useClientId from 'common/hooks/useClientId';

const localeApi: LocusApiModel = new LocusApiModel({
    baseUrl: EnvConfig.platformEndPoint,
    resourceName: 'localeSettings',
    endPoint: '/client/{clientId}/locale/{locale}/locale-settings'
});

const useLocaleConfigQueryOptions = () => {
    const clientId = useClientId();
    const {
        localeSettings: { dateFormat: dateFormatLocale = 'en-GB' }
    } = usePlatformSettings();
    return {
        queryKey: [
            'client',
            clientId,
            'locale',
            dateFormatLocale,
            'locale-settings'
        ],
        queryFn: async () => {
            const response: LocaleConfigResponseModelType = await localeApi.fetch(
                {
                    clientId,
                    locale: dateFormatLocale
                }
            );

            return LocaleConfigResponse.fromJS(response);
        },
        staleTime: Infinity
    };
};

export const useLocaleConfig = (): QueryResult<LocaleConfigResponse> => {
    const queryOptions = useLocaleConfigQueryOptions();
    return useQuery(queryOptions);
};

export const useLocaleConfigPrefetch = (): void => {
    const queryOptions = useLocaleConfigQueryOptions();
    useEffectOnce(() => {
        queryClient.prefetchQuery(queryOptions);
    });
};
